import React from 'react';
import Icon from '../../../components/AppIcon';

const Footer = () => {
  const currentYear = new Date()?.getFullYear();

  const footerSections = [
    {
      title: "Platform",
      links: [
        { name: "Dashboard", href: "/collection-dashboard" },
        { name: "Add Collectible", href: "/add-collectible" },
        { name: "Market Analysis", href: "/market-analysis" },
        { name: "News Feed", href: "/news-feed" }
      ]
    },
    {
      title: "Account",
      links: [
        { name: "Profile", href: "/profile-management" },
        { name: "Sign Up", href: "/registration" },
        { name: "Pricing", href: "/homepage#pricing" },
        { name: "Support", href: "/support" }
      ]
    },
    {
      title: "Resources",
      links: [
        { name: "Authentication Guide", href: "/guides/authentication" },
        { name: "Market Insights", href: "/insights" },
        { name: "Collector Stories", href: "/stories" },
        { name: "API Documentation", href: "/api-docs" }
      ]
    },
    {
      title: "Company",
      links: [
        { name: "About Us", href: "/about" },
        { name: "Careers", href: "/careers" },
        { name: "Press", href: "/press" },
        { name: "Contact", href: "/contact" }
      ]
    }
  ];

  const socialLinks = [
    { name: "Twitter", icon: "Twitter", href: "https://twitter.com/crazyrareclub" },
    { name: "Instagram", icon: "Instagram", href: "https://instagram.com/crazyrareclub" },
    { name: "LinkedIn", icon: "Linkedin", href: "https://linkedin.com/company/crazyrareclub" },
    { name: "YouTube", icon: "Youtube", href: "https://youtube.com/crazyrareclub" }
  ];

  const legalLinks = [
    { name: "Privacy Policy", href: "/privacy" },
    { name: "Terms of Service", href: "/terms" },
    { name: "Cookie Policy", href: "/cookies" },
    { name: "Security", href: "/security" }
  ];

  return (
    <footer className="bg-gradient-to-b from-background to-muted/20 border-t border-border">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-16">
          <div className="grid lg:grid-cols-6 gap-8">
            {/* Brand Section */}
            <div className="lg:col-span-2">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-primary to-warning rounded-xl flex items-center justify-center liquid-gold">
                  <Icon name="Gem" size={20} color="#1A1B23" strokeWidth={2.5} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-foreground font-accent">
                    Crazy Rare Club
                  </h3>
                  <p className="text-sm text-primary">
                    Premium Collectibles Platform
                  </p>
                </div>
              </div>
              
              <p className="text-muted-foreground mb-6 leading-relaxed">
                Where collecting meets investing. Transform your passion into a sophisticated portfolio with professional-grade tools and real-time market intelligence.
              </p>

              {/* Social Links */}
              <div className="flex items-center space-x-4">
                {socialLinks?.map((social) => (
                  <a
                    key={social?.name}
                    href={social?.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-muted/50 rounded-lg flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all duration-300 premium-hover"
                  >
                    <Icon name={social?.icon} size={18} />
                  </a>
                ))}
              </div>
            </div>

            {/* Footer Links */}
            {footerSections?.map((section) => (
              <div key={section?.title}>
                <h4 className="text-lg font-semibold text-foreground mb-4">
                  {section?.title}
                </h4>
                <ul className="space-y-3">
                  {section?.links?.map((link) => (
                    <li key={link?.name}>
                      <a
                        href={link?.href}
                        className="text-muted-foreground hover:text-primary transition-colors duration-200"
                      >
                        {link?.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Newsletter Section */}
        <div className="py-8 border-t border-border">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h4 className="text-xl font-semibold text-foreground mb-2">
                Stay Updated
              </h4>
              <p className="text-muted-foreground">
                Get the latest market insights, authentication tips, and collector success stories delivered to your inbox.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
              <input
                type="email"
                placeholder="Enter your email address"
                className="flex-1 px-4 py-3 bg-input border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              <button className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors duration-200 premium-hover">
                Subscribe
              </button>
            </div>
          </div>
        </div>

        {/* Bottom Footer */}
        <div className="py-8 border-t border-border">
          <div className="flex flex-col lg:flex-row items-center justify-between space-y-4 lg:space-y-0">
            {/* Copyright */}
            <div className="text-muted-foreground text-sm">
              © {currentYear} Crazy Rare Club. All rights reserved.
            </div>

            {/* Legal Links */}
            <div className="flex items-center space-x-6">
              {legalLinks?.map((link, index) => (
                <React.Fragment key={link?.name}>
                  <a
                    href={link?.href}
                    className="text-muted-foreground hover:text-primary text-sm transition-colors duration-200"
                  >
                    {link?.name}
                  </a>
                  {index < legalLinks?.length - 1 && (
                    <span className="text-muted-foreground/50">•</span>
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Trust Badges */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 text-success">
                <Icon name="Shield" size={16} />
                <span className="text-xs font-medium">SSL Secured</span>
              </div>
              <div className="flex items-center space-x-2 text-success">
                <Icon name="Lock" size={16} />
                <span className="text-xs font-medium">GDPR Compliant</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;